import {
  Component,
  effect,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';

import {
  PatientModel,
  PatientPlaylistItemModel,
  PatientPlaylistModel,
  PatientProgramModel,
  PatientTargetModel,
} from '../../../core/api/models';
import { SupervisorApiService } from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { SupervisorHelperService } from '../../supervisor.helper.service';
import { Utils } from '../../../core/utils';
import {
  CdkDragDrop,
  CdkDropList,
  CdkDrag,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
// CKEditor
import { Editor, editorConfig } from '../../../shared/ckeditor/ckeditor-config';
import { OverlayPanel } from 'primeng/overlaypanel';

@Component({
  selector: 'app-add-playlist',
  templateUrl: './add-playlist.component.html',
  styleUrl: './add-playlist.component.css',
  standalone: false,
})
export class AddPlaylistComponent {
  @Input() selectedPatient: PatientModel | null = null;
  @Output() close = new EventEmitter<boolean>();

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      this.selectedItems(),
      event.previousIndex,
      event.currentIndex
    );
  }

  enteredName = signal('');
  starter: number = 0;
  id: number = 0;

  loadingItems: boolean = false;
  availableItems: any[] = [];
  selectedItems = signal<any[]>([]);
  draggedItem: any | undefined | null;

  enteredDetails = signal('');
  public Editor = Editor;
  public editorConfig = editorConfig;

  @ViewChild('subtargetProgramWarningOp')
  subtargetProgramWarningOp?: OverlayPanel;
  subtargetWarningTarget: PatientTargetModel | null = null;
  readonly subtargetProgramWarningTooltip =
    'This target will not appear for use because it is a sub-target. Click for details.';

  constructor(
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    private supervisorHelperService: SupervisorHelperService
  ) {}

  currentPlaylist: PatientPlaylistModel | null = null;

  titleName: string = 'Add Playlist';
  actionName: string = 'Add';

  isSelectedItemSubtarget(item: any): boolean {
    if (item?.ProgramName || item?.ID == null) {
      return false;
    }
    return (
      this.supervisorHelperService
        .targets()
        .find((t) => t.ID === item.ID)?.IsTargetSubtarget === true
    );
  }

  getParentTargetFor(item: any): PatientTargetModel | undefined {
    if (item?.ID == null) {
      return undefined;
    }
    return this.supervisorHelperService
      .targets()
      .find((parent) =>
        parent.SubTargets?.some((subTarget) => subTarget.ID === item.ID)
      );
  }

  toggleSubtargetProgramWarning(event: Event, item: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.subtargetWarningTarget = item;
    this.subtargetProgramWarningOp?.toggle(event);
  }

  editParentTargetFromWarning(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const parent = this.subtargetWarningTarget
      ? this.getParentTargetFor(this.subtargetWarningTarget)
      : undefined;
    this.subtargetProgramWarningOp?.hide();
    if (parent) {
      this.supervisorHelperService.additTargetButtonClicked(parent);
    }
  }

  ngOnInit() {
    this.loadingItems = true;
    const programs = this.supervisorHelperService
      .programs()
      .filter((program) => !program.IsDeleted && !program.IsArchived);
    const targets = this.supervisorHelperService
      .targets()
      .filter((target) => !target.IsDeleted && !target.IsArchived);

    // EDIT LOGIC
    if (this.supervisorHelperService.playlistToEdit() != null) {
      this.titleName = 'Edit Playlist';
      this.actionName = 'Save';
      this.currentPlaylist = this.supervisorHelperService.playlistToEdit();
      this.id = this.currentPlaylist?.ID ?? 0;
      this.enteredName.set(this.currentPlaylist?.PlaylistName ?? '');
      this.enteredDetails.set(this.currentPlaylist?.Details ?? '');
      // Include any historically linked sub-targets so they can be removed.
      this.selectedItems.set(
        [...programs, ...targets].filter((item) => {
          return this.currentPlaylist?.Items?.find(
            (i) =>
              i.PatientProgramID === item.ID || i.PatientTargetID === item.ID
          );
        })
      );
    } else {
      this.selectedItems.set([]);
    }

    const selectedTargetIds = new Set(
      this.selectedItems()
        .filter((item) => !item.ProgramName)
        .map((item) => item.ID)
    );
    this.availableItems = [
      ...programs,
      ...targets.filter(
        (target) =>
          !target.IsTargetSubtarget || selectedTargetIds.has(target.ID)
      ),
    ];

    this.loadingItems = false;
  }

  onCancel() {
    this.currentPlaylist = null;
    this.close.emit(false);
  }

  onSelectedItemsDropdownClick(e: Event) {
    const event = new Event('dropdown-clicked');
    document.getElementById('addPlaylistForm')?.dispatchEvent(event);
  }

  onSubmit() {
    this.currentPlaylist = {
      ID: this.id,
      PlaylistName: this.enteredName(),
      Details: this.enteredDetails(),
      PatientID: this.selectedPatient?.ID,
      Items: this.selectedItems().map((item, index) => {
        if (item.ProgramName) {
          return {
            ID: 0,
            OrderNr: index,
            PatientProgramID: item.ID,
            PatientTargetID: null,
          };
        } else {
          return {
            ID: 0,
            OrderNr: index,
            PatientProgramID: null,
            PatientTargetID: item.ID,
          };
        }
      }),
    };

    console.log(this.currentPlaylist);

    this.supervisorApiService
      .SavePatientPlaylist({ body: this.currentPlaylist })
      .subscribe(
        (savedItem) => {
          this.currentPlaylist = null;
          this.alertService.success('Playlist saved successfully');
          this.close.emit(true);
        },
        (err) => {
          this.alertService.error(Utils.getErrorMessageFromErrorResponse(err));
          this.close.emit(false);
        }
      );
  }

  onDetailsChange(event: any) {
    this.enteredDetails.set(event.editor.getData());
  }
}
